import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Grid, Typography } from "@mui/material";
import { ResponsiveTimeRange } from "@nivo/calendar";
import { ResponsiveChoropleth } from "@nivo/geo";
import {
  useDashboardApplicationListQuery,
  useDashboardHeatMapQuery,
  useDashboardWorldWideQuery,
} from "api/applications/applications.mutation";
import { Table } from "components/Table";
import worldCountries from "constants/world_countries.json";
import jalaali from "jalaali-js";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./style.module.scss";

const dateConverter = (gregorianDate) => {
  const dateParts = gregorianDate.split("-").map((part) => parseInt(part, 10));
  const dateObject = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

  const shamsiDate = jalaali.toJalaali(
    dateObject.getFullYear(),
    dateObject.getMonth() + 1,
    dateObject.getDate()
  );
  return `${shamsiDate.jy}-${shamsiDate.jm}-${shamsiDate.jd}`;
};

const Dashboard = () => {
  const history = useNavigate();

  const [pageFilter, setPageFilter] = useState({
    page: 1,
    page_size: 5,
    total: 1,
    sort: "-create_at",
    count: 0,
  });
  const dashboardApplicationListQuery = useDashboardApplicationListQuery({
    status: "Current",
    page: pageFilter.page,
    page_size: pageFilter.page_size,
  });
  const heatMapQuery = useDashboardHeatMapQuery();
  const worldWideQuery = useDashboardWorldWideQuery();
  const tableData = dashboardApplicationListQuery.data?.results ?? [];
  const heatMapData = useMemo(
    () => (heatMapQuery.data ?? []).filter((item) => item.value !== 0),
    [heatMapQuery.data]
  );
  const dateRange = useMemo(() => {
    const heatMap = heatMapQuery.data ?? [];
    if (heatMap.length === 0) {
      return { earliest: "", latest: "" };
    }

    const dates = heatMap.map((item) => new Date(item.day));
    const earliestDate = new Date(Math.min(...dates));
    const latestDate = new Date(Math.max(...dates));

    return {
      earliest: earliestDate.toISOString().split("T")[0],
      latest: latestDate.toISOString().split("T")[0],
    };
  }, [heatMapQuery.data]);
  const worldData = worldWideQuery.data ?? [];
  const maxValue = useMemo(
    () => ({
      heatMap:
        (heatMapQuery.data ?? []).length > 0
          ? Math.max(...heatMapQuery.data.map((item) => item.value))
          : null,
      world: worldData.length > 0 ? Math.max(...worldData.map((item) => item.value)) : null,
    }),
    [heatMapQuery.data, worldData]
  );

  const columns = [
    {
      field: "tracking_id",
      headerName: "شماره پرونده",
      width: 120,
    },
    {
      field: "full_name",
      headerName: "نام دانشجو",
      renderCell: (node) => node.row?.user?.full_name,
      flex: 1,
    },
    {
      field: "gender",
      headerName: "جنسیت",
      width: 70,
      renderCell: (node) => node.row?.user?.gender,
    },
    {
      field: "age",
      headerName: "سن",
      sortable: false,
      width: 50,
      renderCell: (node) => node.row?.user?.age.toLocaleString("fa-ir"),
    },
    {
      field: "country",
      headerName: "کشور",
      sortable: false,
      renderCell: (node) => node.row?.user?.country,
      flex: 1,
    },
    {
      field: "degree",
      headerName: "مقطع",
      sortable: false,
      flex: 1,
    },
    {
      field: "faculty",
      headerName: "رشته",
      sortable: false,
      flex: 1,
    },
    {
      field: "field_of_study",
      headerName: "گرایش",
      sortable: false,
      flex: 1,
    },
    {
      field: "jalali_created_at",
      headerName: "تاریخ درخواست",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">{row?.jalali_created_at?.split(" - ")[0]}</Typography>
          <Typography variant="caption">{row?.jalali_created_at?.split(" - ")[1]}</Typography>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "وضعیت",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.status,
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      width: 40,
      renderCell: (node) => (
        <div
          className={classes.nextBox}
          onClick={() => history(`/dashboard/all-files/detail/${node.row?.id}`)}
        >
          <ArrowBackIosNewIcon className={classes.nextIcon} fontSize="small" />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={classes.mainDiv}>
        <Grid container spacing={5}>
          <Grid item xs={12} className={classes.fullWidth}>
            <Table
              rows={tableData}
              columns={columns}
              totalPages={pageFilter?.total}
              loading={dashboardApplicationListQuery.isFetching}
            />
          </Grid>
          <Grid item xs={12} lg={6} className={classes.fullWidth}>
            <div className={classes.box}>
              <h3>پراکندگی درخواست ها</h3>
              <div style={{ height: "330px" }}>
                {maxValue.world && (
                  <ResponsiveChoropleth
                    data={worldData}
                    features={worldCountries.features}
                    colors={[
                      "var(--chart-responsive-choropleth-first-color)",
                      "var(--chart-responsive-choropleth-second-color)",
                      "var(--chart-responsive-choropleth-third-color)",
                      "var(--chart-responsive-choropleth-fourth-color)",
                    ]}
                    domain={[0, maxValue.world]}
                    unknownColor="var(--chart-responsive-choropleth-unknown-color)"
                    label="properties.name"
                    valueFormat=".2s"
                    projectionTranslation={[0.5, 0.5]}
                    projectionRotation={[0, 0, 0]}
                    enableGraticule={true}
                    graticuleLineColor="var(--chart-responsive-choropleth-line-color)"
                    borderWidth={0.5}
                    borderColor="var(--auth-button-text)"
                  />
                )}
              </div>
            </div>
          </Grid>
          <Grid item xs={12} lg={6} className={classes.fullWidth}>
            <div className={classes.box}>
              <h3>هیت مپ درخواست های ۳۰ روز اخیر</h3>
              <div style={{ height: "330px" }}>
                {dateRange.latest !== "" && maxValue.heatMap !== null && (
                  <ResponsiveTimeRange
                    data={heatMapData}
                    from={dateRange.earliest}
                    to={dateRange.latest}
                    emptyColor="var(--chart-responsive-choropleth-empty-color)"
                    colors={[
                      "var(--chart-responsive-choropleth-first-color)",
                      "var(--chart-responsive-choropleth-second-color)",
                      "var(--chart-responsive-choropleth-third-color)",
                      "var(--chart-responsive-choropleth-fourth-color)",
                    ]}
                    monthLegendOffset={23}
                    direction="vertical"
                    weekdayLegendOffset={0}
                    firstWeekday="saturday"
                    dayBorderColor="var(--auth-button-text)"
                    maxValue={maxValue.heatMap}
                    tooltip={(e) => (
                      <span className={classes.heatMapToolTip}>
                        {dateConverter(e.day)} : {e.value}
                      </span>
                    )}
                  />
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Dashboard;
